import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { LoadingScreen } from "Components";
import { SavedJobs } from "./SavedJobs";
import { JobDetails } from "./JobDetails";
import { newAuthService } from "Service/Axios";
import {
  getCareerHubJobsFilterListUrl,
  getCareerHubJobsListUrl,
  updateCareerHubSaveJobUrl,
} from "Constant/api";
import { usePagination } from "Utils/hooks";
import { DEFAULT_PAGE_SIZE, SPLIT_CHARACTER } from "Constant/constants";

const CareerHub = () => {
  const { t } = useTranslation();

  const jobsListRef = useRef();
  const loadingRowRef = useRef();

  const [jobsLoading, setJobsLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const [savedJobsModal, setSavedJobsModal] = useState(false);
  const [jobDetailsModal, setJobDetailsModal] = useState(false);
  const [selectJobId, setSelectJobId] = useState("");
  const [selecedSort, setSelectedSort] = useState("Actively Hiring");
  const [totalJobs, setTotalJobs] = useState(0);
  const [filterList, setFilterList] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState([]);
  const [jobsList, setJobsList] = useState([]);

  // Function to handle the modal toggle
  const toggleSavedJobsModal = () => {
    setSavedJobsModal(!savedJobsModal);
  };

  const toggleJobDetailsModal = (gid) => {
    setSelectJobId(gid ? gid : "");
    setJobDetailsModal(!jobDetailsModal);
  };

  // Function to fetch different lists
  const fetchFilterList = async () => {
    setFilterLoading(true);
    const payload = {};
    const response = await newAuthService(
      getCareerHubJobsFilterListUrl,
      "POST",
      payload
    );
    setFilterList(response.data);
    setFilterLoading(false);
  };

  const fetchJobsList = async (filters, sort) => {
    const payload = {
      skip: currPage * DEFAULT_PAGE_SIZE,
      take: DEFAULT_PAGE_SIZE,
    };
    if (filters && filters.length > 0) {
      payload.filters = filters.reduce((acc, filter) => {
        const [key, value] = filter.split(SPLIT_CHARACTER);
        const existingFilter = acc.find((item) => item.filterType === key);
        if (existingFilter) {
          existingFilter.name.push(value);
        } else {
          acc.push({ filterType: key, name: [value] });
        }
        return acc;
      }, []);
    }
    if (sort) {
      payload.sort = sort;
    }
    const response = await newAuthService(
      getCareerHubJobsListUrl,
      "POST",
      payload
    );
    setTotalJobs(response.data.total);
    setJobsList((prevList) => [...prevList, ...response.data.data]);
    setJobsLoading(false);
    return response.data.data;
  };

  const {
    paginationLoading,
    currPage,
    prevPage,
    lastList,
    fetchPaginatedData,
    fetchMoreDataOnScroll,
    goToNextPage,
    resetPagination,
  } = usePagination(fetchJobsList, {
    containerRef: jobsListRef,
    loadingRowRef: loadingRowRef,
  });

  useEffect(() => {
    if (!paginationLoading && !lastList) {
      if (prevPage !== currPage) {
        fetchPaginatedData(selectedFilter, selecedSort);
      } else if (
        jobsListRef.current &&
        jobsListRef.current.scrollHeight > 0 &&
        jobsListRef.current.clientHeight > 0 &&
        jobsListRef.current.scrollHeight <= jobsListRef.current.clientHeight
      ) {
        goToNextPage();
      }
    }
  }, [
    currPage,
    lastList,
    prevPage,
    selectedFilter,
    selecedSort,
    paginationLoading,
  ]);

  useEffect(() => {
    fetchFilterList();
  }, []);

  // Function to handle the filter change
  const handleChangeSort = (e) => {
    const { value } = e.target;

    // Reset pagination state
    handleResetPagination();

    setSelectedSort(value);
  };

  const handleChangeFilter = (e) => {
    const { checked, value } = e.target;
    setSelectedFilter((prev) => {
      const updatedFilters = checked
        ? [...prev, value]
        : prev.filter((filter) => filter !== value);

      // Reset pagination state
      handleResetPagination();

      return updatedFilters;
    });
  };

  // Function to handle the reset filter action
  const handleResetFilter = () => {
    setSelectedFilter([]);
    handleResetPagination();
  };

  const handleResetPagination = () => {
    // Reset pagination state
    setJobsList([]);
    resetPagination();

    // Reset loading state
    setJobsLoading(true);
  };

  // Function to handle the save job action
  const onUpdateSaveJob = async (gid, isSaved) => {
    const payload = {
      gid: gid,
      save: isSaved,
    };
    const response = await newAuthService(
      updateCareerHubSaveJobUrl,
      "POST",
      payload
    );
    if (response.status) {
      toast.success(
        t(
          `common.messages.notification.success.${
            isSaved ? "jobSaved" : "jobRemoved"
          }`
        )
      );
      setJobsList((prevList) =>
        prevList.map((job) =>
          job.gid === gid ? { ...job, save: isSaved } : job
        )
      );
    } else {
      toast.error(t("common.messages.notification.failed.serverError"));
    }
  };

  return (
    <main className="main">
      <div className="rounded-3 bg-white d-md-flex">
        <div className="student-dashboard-filter pt-3 flex-auto rounded-start border border-slate-200 d-flex flex-column row-gap-3">
          <div className="px-3 pb-3 border-bottom border-slate-200 d-flex align-items-center gap-3">
            <h3 className="mb-0 text-base font-semibold font-slate-500">
              Filter
            </h3>
            <button
              type="button"
              className="ms-auto border-0 bg-transparent text-sm font-medium text-red-600"
              disabled={selectedFilter.length === 0}
              onClick={handleResetFilter}
            >
              Clear All
            </button>
          </div>
          <div className="career-hub-filter-box d-flex flex-column row-gap-3 overflow-y-auto">
            {/* <LoadingScreen /> */}
            {filterLoading ? (
              <div className="w-100 h-100 d-flex justify-content-center align-items-center job-loader">
                <LoadingScreen />
              </div>
            ) : filterList === null ? (
              <div className="px-3 d-flex flex-column row-gap-2">
                <h4 className="mb-0 text-base font-bold text-slate-700">
                  No Filters Found
                </h4>
              </div>
            ) : (
              Object.keys(filterList).map((filterKey) => (
                <div
                  className="px-3 d-flex flex-column row-gap-2"
                  key={filterKey.toString()}
                >
                  <h4 className="mb-0 text-base font-bold text-slate-700">
                    {filterKey}
                  </h4>

                  <ul className="list-unstyled mb-0 d-flex flex-column row-gap-2">
                    {filterList[filterKey].map((filter, filterIndex) => (
                      <li className="form-check" key={filterIndex.toString()}>
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`${filterKey}${SPLIT_CHARACTER}${filter}`}
                          value={`${filterKey}${SPLIT_CHARACTER}${filter}`}
                          checked={selectedFilter.includes(
                            `${filterKey}${SPLIT_CHARACTER}${filter}`
                          )}
                          onChange={handleChangeFilter}
                        />
                        <label
                          className="form-check-label text-slate-600"
                          htmlFor={`${filterKey}${SPLIT_CHARACTER}${filter}`}
                        >
                          {filter}
                        </label>
                      </li>
                    ))}
                  </ul>
                </div>
              ))
            )}
          </div>
        </div>
        <div className="job-lists w-100 pl-4 pt-4 d-flex flex-column row-gap-3">
          <div className="d-flex align-items-center gap-3">
            <div className="d-flex align-items-center column-gap-2">
              <h3 className="mb-0 text-base font-bold text-slate-600">
                Total Jobs
              </h3>
              <small className="px-3 py-1 text-sm rounded-pill text-green-700 border border-green-400 bg-green-100">
                {totalJobs} Found
              </small>
            </div>
            <div className="ms-auto d-flex gap-2">
              <div className="ps-2 py-1 rounded border border-slate-200 d-md-flex align-items-center d-none">
                <span className="text-sm font-medium text-slate-500 text-nowrap">
                  Sort by:{" "}
                </span>
                <select
                  className="px-2 py-1 border-0 text-base text-slate-700 form-control"
                  id="selecedSort"
                  name="selecedSort"
                  value={selecedSort}
                  onChange={handleChangeSort}
                >
                  <option value="Actively Hiring">Actively Hiring</option>
                </select>
              </div>
              <button
                type="button"
                className="px-2 py-1 rounded border border-slate-200 d-flex align-items-center column-gap-1 text-sm font-medium text-blue-500 border-blue-200 bg-blue-100 d-md-none"
              >
                <i className="bx bx-slider"></i>
              </button>
              <button
                type="button"
                onClick={toggleSavedJobsModal}
                className="px-2 py-1 rounded border border-slate-200 d-flex align-items-center column-gap-1 text-sm font-medium text-blue-500 border-blue-200 bg-blue-100"
              >
                <span className="d-none d-md-block">Saved Jobs</span>{" "}
                <i className="bx bx-bookmark"></i>
              </button>
            </div>

            {savedJobsModal && (
              <SavedJobs
                show={savedJobsModal}
                toggle={toggleSavedJobsModal}
                toggleJobDetailsModal={toggleJobDetailsModal}
              />
            )}
          </div>

          <div
            className="total-job-list list-unstyled d-flex flex-column row-gap-3 overflow-y-auto"
            ref={jobsListRef}
            onScroll={fetchMoreDataOnScroll}
          >
            {jobsLoading ? (
              <div className="w-100 h-100 d-flex justify-content-center align-items-center job-loader">
                <LoadingScreen />
              </div>
            ) : jobsList && jobsList.length > 0 ? (
              <>
                {jobsList.map((job, index) => (
                  <div
                    key={`${job.gid}-${index}`}
                    // onClick={() => toggleJobDetailsModal(job.gid)}
                  >
                    <div className="p-2 d-flex align-items-center gap-3 rounded-lg border border-slate-200">
                      <div
                        className="w-20 h-20 flex-auto d-flex justify-content-center align-items-center rounded border border-blue-200 bg-blue-200"
                        onClick={() => toggleJobDetailsModal(job.gid)}
                        role="button"
                      >
                        <img
                          src={job.image}
                          alt={job.name}
                          className="w-100 h-100 object-fit-cover"
                        />
                      </div>
                      <div
                        className="d-flex flex-column row-gap-0"
                        onClick={() => toggleJobDetailsModal(job.gid)}
                        role="button"
                      >
                        <h4 className="mb-0 text-sm font-bold text-slate-700">
                          {job.name}{" "}
                        </h4>
                        {job.organizationName && (
                          <span className="text-base text-slate-500">
                            {job.organizationName}
                          </span>
                        )}
                        <ul className="list-unstyled d-flex flex-wrap text-slate-500 list-fill-bullet">
                          <li className="text-xs px-3 position-relative">
                            <span>{job.type}</span>
                          </li>
                          {job.location && (
                            <li className="text-xs px-3 position-relative">
                              <span>{job.location}</span>
                            </li>
                          )}
                        </ul>
                      </div>
                      <div className="d-flex flex-column align-items-end row-gap-2 ms-auto">
                        <button
                          type="button"
                          className="p-0 text-slate-500 border-0 bg-transparent"
                          onClick={() => onUpdateSaveJob(job.gid, !job.save)}
                        >
                          <i
                            className={`bx bx${job.save ? "s" : ""}-bookmark`}
                          ></i>
                        </button>
                        <button
                          type="button"
                          onClick={() => toggleJobDetailsModal(job.gid)}
                          className="btn btn-dark px-4 py-1"
                        >
                          Details
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                {!lastList && (
                  <div ref={loadingRowRef}>
                    <div className="p-2 d-flex justify-content-center align-items-center gap-3 rounded-lg border border-slate-200">
                      <LoadingScreen />
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div>
                <div className="p-2 d-flex align-items-center gap-3 rounded-lg border border-slate-200">
                  <p>No Jobs Found</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {jobDetailsModal && (
          <JobDetails
            show={jobDetailsModal}
            jobId={selectJobId}
            toggle={toggleJobDetailsModal}
          />
        )}
      </div>
    </main>
  );
};

export default CareerHub;
